/* ==========================================================================
   WAVE HAWAII · Bewertungen (Fusion Agency, 29.10.2026)
   1. Text des Judge.me-Buttons "Mehr anzeigen" = "Alle X Bewertungen lesen"
      (X = Anzahl der Bewertungen aus Judge.me). Nach dem ersten Klick: "Weitere Bewertungen anzeigen".
   2. Ab 700 px: Karussell mit 3 sichtbaren Kacheln, Pfeilen und automatischem Weiterlaufen.
      Am Ende lädt es über Judge.me die nächsten Bewertungen nach, nach der letzten geht es wieder an den Anfang.
   Gestaltung in assets/wh-sections.css.
   ========================================================================== */
(() => {
  if (window.whBewertungen) return;
  window.whBewertungen = true;

  const WIDGET = '#judgeme_product_reviews';
  const AUTOPLAY_MS = 5000;
  const desktop = window.matchMedia('(min-width: 700px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const widget = () => document.querySelector(WIDGET);
  const list = () => document.querySelector(`${WIDGET} .jdgm-review-list`);
  const loadMoreButton = () => document.querySelector(`${WIDGET} .jm-load-more-pagination .jm-button`);

  /* ---------- 1. Textlink ---------- */
  const reviewCount = () => {
    try {
      const widgets = window.jdgm && jdgm.data && jdgm.data.reviewWidget;
      if (widgets) {
        const first = Object.values(widgets)[0];
        if (first && first.number_of_reviews) return first.number_of_reviews;
      }
    } catch (e) { /* weiter mit dem sichtbaren Text */ }

    const text = document.querySelector(`${WIDGET} .jm-average-rating-display`)?.textContent || '';
    const m = text.match(/(\d[\d.]*)\s*Bewertung/);
    return m ? parseInt(m[1].replace(/\./g, ''), 10) : null;
  };

  const setLabel = (text) => {
    const w = widget();
    if (w) w.style.setProperty('--wh-reviews-label', JSON.stringify(text));
  };

  const updateLabel = () => {
    const n = reviewCount();
    setLabel(n ? `Alle ${n} Bewertungen lesen` : 'Alle Bewertungen lesen');
  };

  let autoLoading = false; // Nachladen durch das Karussell ändert den Linktext nicht

  document.addEventListener('click', (event) => {
    if (!autoLoading && event.target.closest(`${WIDGET} .jm-load-more-pagination .jm-button`)) {
      setLabel('Weitere Bewertungen anzeigen');
      widget()?.classList.add('wh-rev-expanded'); // mobil ab jetzt alle Bewertungen zeigen
    }
  });

  /* ---------- 2. Karussell ---------- */
  const GAP = 16;
  const stepWidth = (l) => {
    const card = l.firstElementChild;
    return card ? card.getBoundingClientRect().width + GAP : l.clientWidth / 3;
  };
  const remaining = (l) => l.scrollWidth - l.clientWidth - l.scrollLeft;

  let loadingUntil = 0;
  const loadMore = () => {
    const button = loadMoreButton();
    if (!button || Date.now() < loadingUntil) return false;
    loadingUntil = Date.now() + 2000;
    autoLoading = true;
    button.click();
    autoLoading = false;
    return true;
  };

  // Vorrat: wenn weniger als zwei Kacheln rechts übrig sind, die nächsten Bewertungen holen
  const keepStock = () => {
    const l = list();
    if (l && desktop.matches && remaining(l) < stepWidth(l) * 2) loadMore();
  };

  // Weich scrollen; falls der Browser das nicht animiert, nach kurzer Zeit direkt springen
  const goTo = (l, left) => {
    const target = Math.max(0, Math.min(left, l.scrollWidth - l.clientWidth));
    l.scrollTo({ left: target, behavior: 'smooth' });
    setTimeout(() => {
      if (Math.abs(l.scrollLeft - target) > 2) {
        l.style.scrollBehavior = 'auto';
        l.scrollLeft = target;
        l.style.scrollBehavior = '';
      }
    }, 800);
  };

  const next = () => {
    const l = list();
    if (!l) return;
    if (remaining(l) < 4) {
      if (!loadMore()) goTo(l, 0);
      return;
    }
    goTo(l, l.scrollLeft + stepWidth(l));
  };

  const prev = () => {
    const l = list();
    if (!l) return;
    if (l.scrollLeft < 4) goTo(l, l.scrollWidth);
    else goTo(l, l.scrollLeft - stepWidth(l));
  };

  const arrow = (dir) =>
    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${dir === 'prev' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'}"/></svg>`;

  const ensureNav = () => {
    const w = widget();
    if (!w || w.querySelector('.wh-rev-nav')) return;
    const nav = document.createElement('div');
    nav.className = 'wh-rev-nav';
    nav.innerHTML = `<button type="button" class="wh-rev-nav__prev" aria-label="Vorherige Bewertungen">${arrow('prev')}</button><button type="button" class="wh-rev-nav__next" aria-label="Nächste Bewertungen">${arrow('next')}</button>`;
    nav.querySelector('.wh-rev-nav__prev').addEventListener('click', () => { prev(); pauseFor(10000); });
    nav.querySelector('.wh-rev-nav__next').addEventListener('click', () => { next(); pauseFor(10000); });
    // in die Zeile des Textlinks (rechts), sonst unten rechts ins Widget
    (w.querySelector('.jm-load-more-pagination') || w).appendChild(nav);
  };

  let visible = false;
  let hovered = false;
  let pausedUntil = 0;
  const pauseFor = (ms) => { pausedUntil = Date.now() + ms; };

  const tick = () => {
    if (!desktop.matches) return;
    ensureNav();
    keepStock();
    if (reducedMotion.matches || !visible || hovered || Date.now() < pausedUntil || document.hidden) return;
    next();
  };

  const startCarousel = () => {
    const w = widget();
    if (!w) return;
    ensureNav();

    new IntersectionObserver((entries) => { visible = entries.some((e) => e.isIntersecting); }, { threshold: 0.3 }).observe(w);
    w.addEventListener('mouseenter', () => { hovered = true; });
    w.addEventListener('mouseleave', () => { hovered = false; });
    w.addEventListener('focusin', () => { hovered = true; });
    w.addEventListener('focusout', () => { hovered = false; });
    // Eigenes Wischen/Scrollen: kurz pausieren und bei Bedarf nachladen
    w.addEventListener('wheel', () => pauseFor(8000), { passive: true });
    w.addEventListener('touchstart', () => pauseFor(8000), { passive: true });
    w.addEventListener('scroll', () => keepStock(), { passive: true, capture: true });

    setInterval(tick, AUTOPLAY_MS);
  };

  /* ---------- Start ---------- */
  const start = () => {
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (document.querySelector(`${WIDGET} .jdgm-review-list`)) {
        clearInterval(timer);
        updateLabel();
        startCarousel();
      }
      if (tries > 60) clearInterval(timer);
    }, 250);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
